import { Fragment, useCallback, useEffect, useState } from 'react';
import { ApiError, api, type Run, type RunLogEntry } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { InfoHint } from '../components/InfoHint';
import { usePersistedTableState } from '../hooks/usePersistedTableState';
import {
  stageDetailsForDisplay,
  stageDisplayName,
  stageErrorForDisplay,
} from '../lib/reportFormat';

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', { timeStyle: 'medium', dateStyle: 'short' });
}

const LEVEL_CLASS: Record<string, string> = {
  debug: 'pill-neutral',
  info: 'pill-info',
  warning: 'pill-warn',
  error: 'pill-danger',
};

type Props = {
  runId: string;
  run: Run | null;
};

type LogsSortBy = 'created_at' | 'level' | 'stage' | 'message';

type LogsTableState = {
  levelFilter: string;
  stageFilter: string;
  query: string;
  sortBy: LogsSortBy;
  sortDir: 'asc' | 'desc';
};

const LOGS_TABLE_DEFAULTS: LogsTableState = {
  levelFilter: '',
  stageFilter: '',
  query: '',
  sortBy: 'created_at',
  sortDir: 'desc',
};
const LOGS_TABLE_SORTING_KEYS: readonly (keyof LogsTableState)[] = ['sortBy', 'sortDir'];
const LOGS_TABLE_FILTER_KEYS: readonly (keyof LogsTableState)[] = [
  'levelFilter',
  'stageFilter',
  'query',
];

function compareText(
  a: string | null | undefined,
  b: string | null | undefined,
  direction: 'asc' | 'desc',
): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const result = a.localeCompare(b, 'ru');
  return direction === 'asc' ? result : -result;
}

/** Журнал шагов прогона из GET /api/runs/{id}/logs. */
export function RunLogsTab({ runId, run }: Props) {
  const { user } = useAuth();
  const [entries, setEntries] = useState<RunLogEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tableState, setTableState] = usePersistedTableState(
    user?.id,
    `run.${runId}.logs`,
    LOGS_TABLE_DEFAULTS,
    {
      sortingKeys: LOGS_TABLE_SORTING_KEYS,
      filterKeys: LOGS_TABLE_FILTER_KEYS,
      persistSorting: Boolean(user?.persist_table_sorting),
      persistFilters: Boolean(user?.persist_table_filters),
    },
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { levelFilter, stageFilter, query, sortBy, sortDir } = tableState;

  function updateTableState(patch: Partial<LogsTableState>) {
    setTableState((prev) => ({ ...prev, ...patch }));
  }

  function updateSort(key: LogsSortBy) {
    if (sortBy === key) {
      updateTableState({ sortDir: sortDir === 'asc' ? 'desc' : 'asc' });
    } else {
      updateTableState({ sortBy: key, sortDir: 'asc' });
    }
  }

  function SortHeader({ label, value }: { label: string; value: LogsSortBy }) {
    const active = sortBy === value;
    return (
      <button
        type="button"
        className="text-caption text-text-secondary hover:text-text-primary"
        onClick={() => updateSort(value)}
      >
        {label}
        {active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
      </button>
    );
  }

  const load = useCallback(() => {
    api
      .get<RunLogEntry[]>(`/api/runs/${runId}/logs?limit=500`)
      .then(setEntries)
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)));
  }, [runId]);

  useEffect(() => {
    load();
    if (run?.status === 'RUNNING' || run?.status === 'CREATED') {
      const id = window.setInterval(load, 3000);
      return () => window.clearInterval(id);
    }
    return undefined;
  }, [load, run?.status]);

  if (error) {
    return <div className="error-hint">{error}</div>;
  }

  if (entries === null) {
    return <div className="text-text-tertiary">Загрузка журнала…</div>;
  }

  if (entries.length === 0) {
    return (
      <div className="card card-padding text-text-tertiary">
        Записей пока нет. Они появятся после старта pipeline.
      </div>
    );
  }

  const stages = Array.from(new Set(entries.map((e) => e.stage).filter(Boolean))).sort();
  const levels = Array.from(new Set(entries.map((e) => e.level))).sort();
  const filtered = entries
    .filter((e) => {
      if (levelFilter && e.level !== levelFilter) return false;
      if (stageFilter && e.stage !== stageFilter) return false;
      if (query.trim()) {
        const haystack = `${e.message} ${e.stage ?? ''} ${JSON.stringify(e.payload ?? {})}`.toLowerCase();
        if (!haystack.includes(query.trim().toLowerCase())) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'created_at') {
        const aTime = new Date(a.created_at).getTime();
        const bTime = new Date(b.created_at).getTime();
        return sortDir === 'asc' ? aTime - bTime : bTime - aTime;
      }
      if (sortBy === 'level') return compareText(a.level, b.level, sortDir);
      if (sortBy === 'stage') return compareText(stageDisplayName(a.stage), stageDisplayName(b.stage), sortDir);
      return compareText(a.message, b.message, sortDir);
    });

  async function copyPayload(payload: Record<string, unknown> | null) {
    if (!payload) return;
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="inline-flex items-center gap-2">
        <div className="rubric">Журнал pipeline</div>
        <InfoHint text="Журнал показывает технические шаги прогона, статусы внешних API и payload без секретов." />
      </div>
      <div className="card card-padding grid md:grid-cols-4 gap-3">
        <input
          className="input"
          value={query}
          onChange={(e) => updateTableState({ query: e.target.value })}
          placeholder="Поиск по журналу и payload"
        />
        <select
          className="input"
          value={stageFilter}
          onChange={(e) => updateTableState({ stageFilter: e.target.value })}
        >
          <option value="">Все стадии</option>
          {stages.map((stage) => (
            <option key={stage} value={stage ?? ''}>{stageDisplayName(stage)}</option>
          ))}
        </select>
        <select
          className="input"
          value={levelFilter}
          onChange={(e) => updateTableState({ levelFilter: e.target.value })}
        >
          <option value="">Все уровни</option>
          {levels.map((level) => (
            <option key={level} value={level}>{level}</option>
          ))}
        </select>
        <div className="text-caption text-text-tertiary flex items-center">
          Показано {filtered.length} из {entries.length}
        </div>
      </div>

      <div className="table-wrap max-h-[70vh] overflow-auto">
        <table className="table">
          <thead>
            <tr>
              <th><SortHeader label="Время" value="created_at" /></th>
              <th><SortHeader label="Уровень" value="level" /></th>
              <th><SortHeader label="Стадия" value="stage" /></th>
              <th><SortHeader label="Сообщение" value="message" /></th>
              <th>Детали</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => {
              const expanded = expandedId === e.id;
              const displayMessage = stageErrorForDisplay(e.stage, e.message) ?? e.message;
              const displayPayload = stageDetailsForDisplay(e.stage, e.payload);
              const hasPayload = displayPayload && Object.keys(displayPayload).length > 0;
              return (
                <Fragment key={e.id}>
                  <tr
                    className="cursor-pointer hover:bg-surface-2"
                    onClick={() => setExpandedId(expanded ? null : e.id)}
                  >
                    <td className="text-caption whitespace-nowrap">{formatTime(e.created_at)}</td>
                    <td>
                      <span className={LEVEL_CLASS[e.level] ?? 'pill-neutral'}>{e.level}</span>
                    </td>
                    <td className="text-caption">{stageDisplayName(e.stage)}</td>
                    <td className="text-text-primary text-caption">{displayMessage}</td>
                    <td className="text-caption text-text-tertiary">
                      {hasPayload ? (expanded ? 'Скрыть' : 'Открыть') : '—'}
                    </td>
                  </tr>
                  {expanded && hasPayload ? (
                    <tr key={`${e.id}-payload`}>
                      <td colSpan={5}>
                        <div className="card card-padding bg-surface-2 flex flex-col gap-2">
                          <div className="flex items-center justify-between gap-2">
                            <div className="rubric">Payload</div>
                            <button
                              type="button"
                              className="btn-ghost btn-sm"
                              onClick={(event) => {
                                event.stopPropagation();
                                void copyPayload(displayPayload);
                              }}
                            >
                              Copy JSON
                            </button>
                          </div>
                          <pre className="mono text-caption text-text-secondary overflow-auto max-h-[320px] whitespace-pre-wrap">
                            {JSON.stringify(displayPayload, null, 2)}
                          </pre>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
